import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Sentry } from '@/sentry';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

interface Props {
    children: ReactNode;
    sectionName: string;
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
    showRetry?: boolean;
    showDetails?: boolean;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

class SectionErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    static getDerivedStateFromError(error: Error): State {
        return {
            hasError: true,
            error,
            errorInfo: null,
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        this.setState({
            errorInfo,
        });

        console.error(
            `SectionErrorBoundary (${this.props.sectionName}) caught an error:`,
            error,
            errorInfo
        );

        Sentry.captureException(error, {
            tags: { section: this.props.sectionName },
            extra: { componentStack: errorInfo.componentStack },
        });

        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }
    }

    handleRetry = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
        });
    };

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/20">
                    <CardHeader className="pb-3">
                        <div className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                            <CardTitle className="text-lg text-red-800 dark:text-red-200">
                                {this.props.sectionName} Error
                            </CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <p className="text-sm text-red-700 dark:text-red-300">
                            Something went wrong while loading{' '}
                            {this.props.sectionName.toLowerCase()}.
                        </p>

                        {this.props.showDetails &&
                            process.env.NODE_ENV === 'development' &&
                            this.state.error && (
                                <details className="bg-red-100 dark:bg-red-900/20 rounded-lg p-2">
                                    <summary className="text-sm font-medium text-red-800 dark:text-red-200 cursor-pointer">
                                        Error Details
                                    </summary>
                                    <div className="text-xs text-red-600 dark:text-red-400 font-mono mt-2">
                                        <div className="mb-1">
                                            <strong>Error:</strong> {this.state.error.message}
                                        </div>
                                        {this.state.errorInfo && (
                                            <div>
                                                <strong>Stack:</strong>
                                                <pre className="whitespace-pre-wrap mt-1 text-xs">
                                                    {this.state.errorInfo.componentStack}
                                                </pre>
                                            </div>
                                        )}
                                    </div>
                                </details>
                            )}

                        {this.props.showRetry && (
                            <Button
                                onClick={this.handleRetry}
                                variant="outline"
                                size="sm"
                                className="border-red-300 text-red-700 hover:bg-red-100 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/20"
                            >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Try Again
                            </Button>
                        )}
                    </CardContent>
                </Card>
            );
        }

        return this.props.children;
    }
}

export default SectionErrorBoundary;
